import React, { ReactElement } from "react";
import { Column } from "./BaseTable";
import RegularTable from "./RegularTable";
import TransposedTable from "./TransposedTable";
import styled from "styled-components";

interface TableStylesProps {
  className?: string;
  children?: React.ReactNode;
}

// Global table-related styles
const TableStyles = styled.div<TableStylesProps>`
  .font-weight-bold {
    font-weight: bold;
  }
`;

export interface TableProps<T = any> {
  id?: string;
  columns: Column<T>[];
  data: T[];
  includeFooter?: boolean;
  filterNullAnd0Columns?: boolean;
  rotateHeaders?: boolean;
  rotate?: boolean;
  regularStyles?: boolean;
  rowLabelColumn?: keyof T;
  extraStyle?: string;
  showHeader?: boolean; // on transposed tables, the header is added to the first row
  highlightColumn?: boolean;
  compact?: boolean;
  autoAlignCurrency?: boolean;
  className?: string;
  appendEmptyCells?: boolean;
  lastIsTotal?: boolean;
  "aria-labelledby"?: string;
  showCrossOrCheck?: boolean;
}

const Table = <T extends Record<string, any>>({
  id,
  columns,
  data,
  compact,
  includeFooter = false,
  filterNullAnd0Columns = true,
  rotateHeaders = false,
  rotate = false,
  regularStyles,
  rowLabelColumn,
  extraStyle,
  showHeader = true,
  highlightColumn,
  autoAlignCurrency = false,
  className,
  appendEmptyCells,
  lastIsTotal = false,
  showCrossOrCheck = false,
  "aria-labelledby": ariaLabelledby
}: TableProps<T>): ReactElement => {
  if (!columns || columns.length === 0 || !data || data.length === 0) {
    throw new Error("columns and/or data can't be null, undefined or empty");
  }

  if (rotate) {
    return (
      <TableStyles className={`${className || ''}`.trim()}>
        <TransposedTable
          id={id}
          columns={columns}
          data={includeFooter ? data.slice(0, -1) : data}
          filterNullAnd0Columns={filterNullAnd0Columns}
          rotateHeaders={rotateHeaders}
          rowLabelColumn={rowLabelColumn}
          extraStyle={extraStyle}
          showHeader={showHeader}
          highlightColumn={highlightColumn}
          autoAlignCurrency={autoAlignCurrency}
          className={className}
          appendEmptyCells={appendEmptyCells}
          showCrossOrCheck={showCrossOrCheck}
          regularStyles={regularStyles}
        />
      </TableStyles>
    );
  }

  return (
    <TableStyles className={`break-avoid ${className || ''}`.trim()}>
      <RegularTable
        id={id}
        columns={columns}
        data={data}
        includeFooter={includeFooter}
        filterNullAnd0Columns={filterNullAnd0Columns}
        rotateHeaders={rotateHeaders}
        extraStyle={extraStyle}
        compact={compact}
        className={className}
        lastIsTotal={lastIsTotal}
        aria-labelledby={ariaLabelledby}
        showHeader={showHeader}
      />
    </TableStyles>
  );
};

export default Table;
